import { useMemo, useState } from 'react';

import { DateRangePicker, Input, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { SearchFilter } from '~/components/common';

import { getAllTemperatureOptions } from '~/utils';

import { OPTION_ALL, RECEIVE_HAS_REPORT, RECEIVE_REPORT_STATUS } from '~/constants';

import type {
  TypeReceiveFilterOptions,
  TypeReceiveStatusFilter,
  TypeReceiveStatusFilterStatus,
} from '~/types/receive.types';

import styles from './ReceiveStatusSearchFilter.module.scss';

const ReceiveStatusSearchFilter = ({
  options,
  values,
  valuesState,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
  reportFilters,
}: {
  options?: TypeReceiveFilterOptions;
  values: TypeReceiveStatusFilter;
  valuesState: TypeReceiveStatusFilterStatus;
  onChange: (values: Partial<TypeReceiveStatusFilter>) => void;
  onChangeStatus: (values: Partial<TypeReceiveStatusFilterStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
  reportFilters?: {
    values: {
      hasReport: TypeSelectOption<string>;
      reportStatus: TypeSelectOption<string>;
    };
    onChange: (
      values: Partial<{ hasReport: TypeSelectOption<string>; reportStatus: TypeSelectOption<string> }>
    ) => void;
  };
}) => {
  const [optionTemperature, setOptionTemperature] = useState<TypeSelectOption<string>[]>([OPTION_ALL]);

  /**
   * '리포트 유무' 값에 따른 '리포트 상태' 옵션
   * - 전체: 전체만 표시
   * - 있음(YES): 리포트 미발행(NONE) 제외
   * - 없음(NO): 리포트 미발행(NONE)만 표시
   */
  const filteredReportStatusOptions = useMemo(() => {
    if (!reportFilters || !options?.reportStatus) return [];

    const hasReportValue = reportFilters.values.hasReport?.value;

    if (hasReportValue === OPTION_ALL.value) {
      return options.reportStatus.filter((option) => option.value === OPTION_ALL.value);
    }
    if (hasReportValue === RECEIVE_HAS_REPORT.YES.value) {
      return options.reportStatus.filter((option) => option.value !== RECEIVE_REPORT_STATUS.NONE.value);
    }
    if (hasReportValue === RECEIVE_HAS_REPORT.NO.value) {
      return options.reportStatus.filter((option) => option.value === RECEIVE_REPORT_STATUS.NONE.value);
    }
    return options.reportStatus;
  }, [reportFilters?.values.hasReport, options?.reportStatus]);

  return (
    <SearchFilter formId="receive-status-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text weight="semi-bold" size="small" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            placeholder="선택"
            options={options?.keywordType ?? []}
            value={values.keywordType}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
            }}
          />
          <Input
            type="text"
            placeholder="입력"
            size="small"
            value={values.keyword}
            maxLength={200}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
          />
        </div>
      </div>

      <div className={clsx(styles['filter-container'], styles['filter-center'])}>
        <Text weight="semi-bold" size="small" element="p">
          입고 센터
        </Text>
        <div className={styles['input-container']}>
          <Select
            placeholder="선택"
            options={options?.center ?? []}
            value={values.center}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.center?.value) return;
              onChange({ center: value, temperature: OPTION_ALL });
              setOptionTemperature(
                value.value === OPTION_ALL.value
                  ? getAllTemperatureOptions(options?.groupedTemperatureByCenter ?? {})
                  : (options?.groupedTemperatureByCenter[value.value] ?? [])
              );
            }}
          />
          <Select
            placeholder="선택"
            disabled={values.center.value === OPTION_ALL.value}
            options={optionTemperature}
            value={values.temperature}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.temperature?.value) return;

              const currentCenter = Object.keys(options?.groupedTemperatureByCenter ?? {}).find(
                (key) => options?.groupedTemperatureByCenter[key]?.includes(value) ?? false
              );

              onChange({
                center: options?.center.find(({ value }) => value === currentCenter) ?? OPTION_ALL,
                temperature: value,
              });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text weight="semi-bold" size="small" element="p">
          기간
        </Text>
        <div className={styles['input-container']}>
          <Select
            placeholder="선택"
            options={options?.dateType ?? []}
            value={values.dateType}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.dateType?.value) return;
              onChange({ dateType: value });
            }}
          />
          <DateRangePicker
            size="small"
            placeholder={{
              start: '시작일',
              end: '종료일',
            }}
            status={valuesState.dateRange}
            value={values.dateRange}
            onChange={(value) => {
              onChange({ dateRange: value });
              onChangeStatus({ dateRange: 'default' });
            }}
            onFocus={() => onChangeStatus({ dateRange: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-status'])}>
        <Text weight="semi-bold" size="small" element="p">
          요청 상태
        </Text>
        <Select
          placeholder="선택"
          options={options?.status ?? []}
          value={values.status}
          size="small"
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.status?.value) return;
            onChange({ status: value });
          }}
        />
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-release-code'])}>
        <Text weight="semi-bold" size="small" element="p">
          입고 방법
        </Text>
        <Select
          placeholder="선택"
          options={options?.releaseCode ?? []}
          value={values.releaseCode}
          size="small"
          onChange={(_: unknown, value: TypeSelectOption<string>) => {
            if (value?.value === values.releaseCode?.value) return;
            onChange({ releaseCode: value });
          }}
        />
      </div>
      {!reportFilters && (
        <div className={clsx(styles['filter-container'], styles['filter-disallow-all-yes-no'])}>
          <Text weight="semi-bold" size="small" element="p">
            문제 유무
          </Text>
          <Select
            placeholder="선택"
            options={options?.disallow ?? []}
            value={values.disallowAllYesNo}
            size="small"
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.disallowAllYesNo?.value) return;
              onChange({ disallowAllYesNo: value });
            }}
          />
        </div>
      )}
      {reportFilters && (
        <>
          <div className={clsx(styles['filter-container'], styles['filter-report-all-yes-no'])}>
            <Text weight="semi-bold" size="small" element="p">
              리포트 유무
            </Text>
            <Select
              placeholder="선택"
              options={options?.reportAllYesNo ?? []}
              value={reportFilters.values.hasReport}
              size="small"
              onChange={(_: unknown, option: TypeSelectOption<string>) => {
                if (option?.value === reportFilters.values.hasReport?.value) return;

                let defaultReportStatus: TypeSelectOption<string>;

                if (option?.value === RECEIVE_HAS_REPORT.NO.value) {
                  defaultReportStatus = RECEIVE_REPORT_STATUS.NONE;
                } else {
                  defaultReportStatus = OPTION_ALL;
                }

                reportFilters.onChange({
                  hasReport: option,
                  reportStatus: defaultReportStatus,
                });
              }}
            />
          </div>
          <div className={clsx(styles['filter-container'], styles['filter-report-status'])}>
            <Text weight="semi-bold" size="small" element="p">
              리포트 상태
            </Text>
            <Select
              placeholder="선택"
              disabled={reportFilters.values.hasReport.value !== RECEIVE_HAS_REPORT.YES.value}
              options={filteredReportStatusOptions}
              value={reportFilters.values.reportStatus}
              size="small"
              onChange={(_: unknown, value: TypeSelectOption<string>) => {
                if (value?.value === reportFilters.values.reportStatus?.value) return;
                reportFilters.onChange({ reportStatus: value });
              }}
            />
          </div>
        </>
      )}
    </SearchFilter>
  );
};

export default ReceiveStatusSearchFilter;
